<template>
  <Intersect :threshold="0.5" @enter="onIntersect">
    <div
      v-editable="blok"
      class="flex size-full flex-col items-center justify-center gap-4 lg:flex-row lg:justify-around"
      :class="{
        'px-10 py-5': isSmall,
        'p-10 lg:px-20': !isSmall,
      }"
      :style="{ backgroundColor }"
    >
      <div
        v-if="blok.headline"
        class="w-full text-center lg:text-left"
        :class="[headlineClassNames]"
        :style="{ color }"
      >
        <RichTextRenderer :document="blok.headline" />
      </div>
      <div
        class="flex flex-col items-center justify-center gap-6 max-sm:w-full md:flex-row"
      >
        <FimButton
          v-for="(button, i) in blok.buttons"
          :id="'hero-' + idPrefix + button._uid"
          :key="i"
          class="w-full"
          :to="ctaURLHandler(button, $currentShop)"
          :target="button.cta_target_blank ? '_blank' : '_self'"
          :type="toButtonType(button.cta_variant)"
          :data-testid="`button_${button._uid}`"
          :theme="theme"
          :style="{ color: linkColor(button) }"
          @mousedown="trackClick"
        >
          {{ button.cta_label }}
        </FimButton>
      </div>
    </div>
  </Intersect>
</template>

<script setup lang="ts">
import type { SbButtonBox, SbLink } from '../types/storyblok.gen'
import { getHeadlineClassNames } from '~/storyblok/utils/cssClassesHelpers'
import { ctaURLHandler } from '~/storyblok/utils/links'

const props = defineProps({
  blok: {
    type: Object as PropType<SbButtonBox>,
    required: true,
  },
  idPrefix: {
    type: String as PropType<string>,
    default: '',
  },
  isSmall: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
})

const { trackButtonClick, trackPromotionClick, trackPromotionView } =
  await useTrackingEvents()
const trackClick = (event: Event) => {
  const element = event?.target as HTMLButtonElement
  trackButtonClick(element)

  if (isStoryblokTrackingContent(props.blok)) {
    trackPromotionClick(props.blok)
  }
}

const headlineClassNames = computed(() =>
  getHeadlineClassNames(props.blok.headline_look || 'h1'),
)

const theme = computed(() => toTheme(props.blok.theme))

const backgroundColor = computed(() => {
  return (
    props.blok.background_color ||
    (theme.value === 'dark' ? 'var(--primary-500)' : 'transparent')
  )
})

const color = computed(
  () =>
    props.blok.headline_color ||
    (theme.value === 'dark' ? 'var(--secondary-100)' : 'var(--primary-500)'),
)

const linkColor = (button: SbLink) => {
  const buttonType = toButtonType(button.cta_variant) || ''
  const isLink = ['link', 'link-alternative'].includes(buttonType)

  return isLink
    ? theme.value === 'dark'
      ? 'var(--secondary-100)'
      : 'var(--primary-500)'
    : null
}

const $currentShop = useCurrentShop()

const onIntersect = (_: IntersectionObserverEntry, stop: () => void) => {
  if (isStoryblokTrackingContent(props.blok)) {
    trackPromotionView(props.blok)
  }
  stop()
}
</script>
